// src/pages/transport/TransportBookingPage.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
    Container,
    Paper,
    Stack,
    Title,
    Text,
    Loader,
    Alert,
    MantineProvider,
} from "@mantine/core";
import TransportBookingStepper from "./TransportBookingStepper";
import { TransportInitResponseDto } from "./types";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
    da: {
        missingEventId: "Mangler eventId i linket.",
        loadBookingError: "Kunne ikke indlæse transport-booking.",
        missingToken: "Mangler token i linket.",
        loading: "Indlæser…",
        errorTitle: "Fejl",
        unknownError: "Ukendt fejl",
        transportOrder: "Transportbestilling",
        publicBooking: "Offentlig booking",
    },
    en: {
        missingEventId: "Missing eventId in the link.",
        loadBookingError: "Could not load transport booking.",
        missingToken: "Missing token in the link.",
        loading: "Loading…",
        errorTitle: "Error",
        unknownError: "Unknown error",
        transportOrder: "Transport booking",
        publicBooking: "Public booking",
    },
};

export default function TransportBookingPage() {
    const [params] = useSearchParams();
    const token = params.get("token") ?? "";
    const eventId = params.get("eventId") ?? "";

    const [data, setData] = useState<TransportInitResponseDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const { language } = useLanguage();

    const currentLanguage = token === "new" ? "da" : "en";
    const t = translations[currentLanguage];

    useEffect(() => {
        const run = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;
                setLoading(true);
                setError(null);

                const requestParams: Record<string, string> = { token };

                if (token === "new") {
                    if (!eventId) {
                        setError(t.missingEventId);
                        setLoading(false);
                        return;
                    }

                    requestParams.eventId = eventId;
                }

                const res = await axios.get(
                    `${apiUrl}/api/transport/booking/init`,
                    {
                        params: requestParams,
                    }
                );

                setData(res.data);
            } catch (e: any) {
                setError(e?.response?.data ?? t.loadBookingError);
            } finally {
                setLoading(false);
            }
        };

        if (!token) {
            setError(t.missingToken);
            setLoading(false);
            return;
        }

        run();
    }, [token, eventId, t]);

    if (loading) {
        return (
            <Container size="sm" py="xl">
                <Paper withBorder p="lg">
                    <Stack>
                        <Loader />
                        <Text>{t.loading}</Text>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container size="sm" py="xl">
                <Alert color="red" title={t.errorTitle}>
                    {error ?? t.unknownError}
                </Alert>
            </Container>
        );
    }

    return (
        <Container size="md" py="xl">
            <Paper withBorder p="lg" radius="md">
                <Stack gap="xs">
                    <Title order={3}>{t.transportOrder}</Title>
                    <Text c="dimmed">
                        {data.isPublicBooking
                            ? `${t.publicBooking} · ${data?.event?.name ?? "—"}`
                            : `${data?.person?.name ?? "—"} · ${data?.event?.name ?? "—"}`}
                    </Text>
                </Stack>
            </Paper>

            <Paper withBorder p="lg" radius="md" mt="md">
                <MantineProvider
                    theme={{
                        primaryColor: "brand",
                        colors: {
                            brand: [
                                "#ffe5e5",
                                "#ffcaca",
                                "#ff9a9a",
                                "#ff7a7a",
                                "#ff6868",
                                "#ff5e5e",
                                "#f24848",
                                "#e53a3a",
                                "#d92d2d",
                                "#cc2222",
                            ],
                        },
                    }}
                >
                    <TransportBookingStepper init={data} token={token} />
                </MantineProvider>
            </Paper>
        </Container>
    );
}